'use client'

import React, { useSyncExternalStore } from "react"
import { createPortal } from "react-dom"
import { useRouter, usePathname } from "next/navigation"
import { useGiftCardPaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS } from "@/lib/commerce/hooks"
import { STATUS_LABELS } from "@/utils/gift"
import { navigateUrl } from "@/utils/client-request"
import QRCodeImport from "react-qr-code"
const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>
import Image from "next/image"
import qpayLogo from "@/assets/qpay.svg"
import { formatDateTimeLong, formatImageURL, formatPrice } from "@/utils/util"
import { ArrowRight, ExternalLink } from "lucide-react"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const solidButton = `inline-flex items-center justify-center gap-2 h-12 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`
const sweepLink = `pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${focusRing}`
const QR_INK = '#09090b'
const subscribeNoop = () => () => {}

const recipientOf = (giftCard: any) => giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()

function StatusPill({ status }: { status: string }) {
  const label = STATUS_LABELS[status] || 'Тодорхойгүй'
  const isAlert = status === 'canceled'
  return (
    <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-foreground">
      <span className={`relative inline-flex size-1.5 rounded-full ${isAlert ? 'bg-red-600 text-red-600' : 'bg-foreground text-foreground'}`}>
        {status === 'pending' && <span className="ping-ring" />}
      </span>
      {label}
    </span>
  )
}

function InfoRow({ label, children }: { label: string, children: any }) {
  return (
    <div className="py-3 grid grid-cols-[110px_minmax(0,1fr)] gap-4 items-baseline">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-[13px] wrap-break-word">{children}</dd>
    </div>
  )
}

function GiftTitle({ giftCard, status }: { giftCard: any, status: string }) {
  const recipient = recipientOf(giftCard)
  return (
    <header>
      <div className={eyebrow}>Бэлгийн карт</div>
      <h1 className="mt-3 text-4xl sm:text-5xl font-light tracking-tight tabular-nums text-foreground">#{giftCard?.customId}</h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <StatusPill status={status} />
        <span className="text-[13px] text-muted-foreground tabular-nums">
          {formatDateTimeLong(giftCard?.createdAt)}
          {recipient ? ` · ${recipient}` : ''}
        </span>
      </div>
    </header>
  )
}

function PaidBanner({ giftCard, status }: { giftCard: any, status: string }) {
  const isUsed = status === 'used'
  const isSent = status === 'sent'
  const label = isUsed ? 'Карт ашиглагдсан' : isSent ? 'Карт илгээгдсэн' : 'Төлбөр амжилттай'
  const title = isUsed ? 'Хүлээн авагч ашигласан' : isSent ? 'Илгээгдлээ' : 'Захиалга баталгаажигдлаа'
  const subtitle = isUsed ? 'Бэлгийн карт амжилттай ашиглагдсан байна.'
    : isSent ? 'Хүлээн авагчийн имэйл рүү бэлгийн картыг илгээлээ.'
      : 'Илгээх өдөр хүлээн авагчид имэйлээр бэлгийн карт хүргэгдэнэ.'
  const tiles = [
    { label: 'Карт', value: `#${giftCard?.customId}` },
    { label: 'Үнийн дүн', value: formatPrice(giftCard?.amount) },
    { label: 'Илгээх өдөр', value: giftCard?.deliveryDate ? formatDateTimeLong(giftCard.deliveryDate) : '—' },
  ]
  return (
    <section className="py-8 border-y border-border">
      <div className={eyebrow}>{label}</div>
      <h2 className="mt-3 text-2xl sm:text-3xl tracking-tight text-foreground">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
      <dl className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
        {tiles.map(tile => (
          <div key={tile.label}>
            <dt className={eyebrow}>{tile.label}</dt>
            <dd className="mt-2 text-base tabular-nums text-foreground">{tile.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function CanceledBanner({ giftCard }: { giftCard: any }) {
  return (
    <div className="border-l border-foreground pl-4">
      <div className="text-sm text-foreground">Цуцлагдсан</div>
      <div className="mt-1 text-xs text-muted-foreground tabular-nums">
        Бэлгийн картын захиалга цуцлагдсан байна
        {giftCard?.canceledAt ? ` · ${formatDateTimeLong(giftCard.canceledAt)}` : ''}
      </div>
    </div>
  )
}

function QRFrame({ value, logoSrc, size = 200 }: { value: string, logoSrc?: any, size?: number }) {
  if (!value) return <div className="bg-muted animate-pulse" style={{ width: size + 24, height: size + 24 }} />
  return (
    <div className="relative border border-border bg-white p-3" style={{ width: size + 24, height: size + 24 }}>
      <QRCode value={value} size={size - 8} fgColor={QR_INK} bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />
      {logoSrc && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-11 bg-white border border-border grid place-items-center p-1">
          <Image src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  )
}

function BankChips({ banks }: { banks: any[] }) {
  const visible = banks.slice(0, 8)
  const hidden = banks.slice(8)
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="flex items-center -space-x-2">
        {visible.map((b: any, i: number) => (
          <div key={i} className="size-7 rounded-full border-2 border-background bg-white overflow-hidden shrink-0" title={b.name}>
            <img src={b.logo} alt={b.name || ''} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
      {hidden.length > 0 && <span className="text-xs text-muted-foreground tabular-nums">+{hidden.length}</span>}
    </div>
  )
}

function PaymentSuccessCard() {
  return (
    <section className="py-6 border-y border-border">
      <div className={eyebrow}>Төлбөр</div>
      <div className="mt-3 text-lg tracking-tight text-foreground">Төлбөр амжилттай төлөгдлөө</div>
      <p className="mt-1 text-[13px] text-muted-foreground">Бэлгийн карт баталгаажиж, илгээх өдөр хүлээн авагчид имэйлээр хүргэгдэнэ.</p>
    </section>
  )
}

function PendingPayment({ giftCard, onSuccess }: { giftCard: any, vendor?: any, onSuccess: () => void }) {
  const { loading, success, check } = useGiftCardPaymentChecker({ giftCard, onSuccess })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ giftCard })

  if (success) return <PaymentSuccessCard />
  if (!giftCard?.qpay) return null

  const banks = giftCard?.qpay?.urls || []

  return (
    <section className="border border-border">
      <div className="px-5 py-4 flex items-center gap-4 border-b border-border">
        <div className="size-10 bg-muted grid place-items-center shrink-0 p-2 overflow-hidden">
          <Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />
        </div>
        <div className="min-w-0">
          <div className={eyebrow}>Төлбөрийн арга</div>
          <div className="mt-1 text-base text-foreground">QPay <span className="text-muted-foreground">· QR төлбөр</span></div>
        </div>
      </div>

      <div className="grid sm:grid-cols-[auto_1fr] gap-6 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={giftCard?.qpay?.QRCode} logoSrc={qpayLogo} size={200} />
          <div className="mt-2 text-[11px] text-muted-foreground">Карт <span className="font-mono text-foreground">#{giftCard?.customId}</span></div>
        </div>
        <div className="flex flex-col">
          <div className={eyebrow}>Төлөх дүн</div>
          <div className="mt-2 text-3xl font-light tabular-nums tracking-tight text-foreground">{formatPrice(giftCard?.amount)}</div>
          <ol className="mt-5 space-y-2.5 text-[13px] text-foreground">
            {['Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу', 'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу'].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-5 text-xs tabular-nums text-muted-foreground pt-px">{String(i + 1).padStart(2, '0')}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <button type="button" onClick={check} disabled={loading} className={`mt-6 w-full ${solidButton}`}>
            {loading ? 'Шалгаж байна…' : 'Төлбөр шалгах'}
          </button>
        </div>
      </div>

      {banks.length > 0 && (
        <>
          <div className="px-5 pb-5 sm:hidden">
            <div className={`mb-3 ${eyebrow}`}>Банкны апп шууд нээх</div>
            {isInAppBrowser && (
              <div className="mb-3 border border-border p-4">
                <p className="text-[13px] text-foreground">{IN_APP_BROWSER_LABELS.message}</p>
                <a href={browserUrl} onClick={openInBrowser} className={`mt-4 w-fit ${solidButton}`}>
                  {IN_APP_BROWSER_LABELS.action}<ExternalLink className="size-3.5" strokeWidth={1.5} />
                </a>
                <p className="mt-3 text-xs text-muted-foreground">{IN_APP_BROWSER_LABELS.hint}</p>
              </div>
            )}
            {!appLinksBlocked && (
              <div className="grid grid-cols-6 gap-1">
                {banks.map((b: any, i: number) => (
                  <a key={i} href={b.link} className={`aspect-square bg-white border border-border overflow-hidden transition-colors duration-150 hover:border-foreground ${focusRing}`}>
                    <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="hidden sm:flex px-5 py-4 items-center justify-between border-t border-border gap-3">
            <div className="text-xs text-muted-foreground shrink-0">Дэмжигдсэн банкууд</div>
            <BankChips banks={banks} />
          </div>
        </>
      )}
    </section>
  )
}

function GiftItem({ giftCard }: { giftCard: any }) {
  return (
    <section>
      <h2 className={eyebrow}>Захиалсан карт</h2>
      <div className="mt-2 py-6 border-y border-border flex gap-5">
        <div className="w-32 sm:w-40 shrink-0 aspect-3/2 bg-muted overflow-hidden">
          <img src={formatImageURL(giftCard?.template?.thumbnail)} alt={giftCard?.template?.name || ''} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm text-foreground truncate">{giftCard?.template?.name}</div>
            <div className="mt-1 text-xs text-muted-foreground">Бэлгийн карт · 1 ширхэг</div>
            {giftCard?.message && <p className="mt-3 text-[13px] italic leading-relaxed text-muted-foreground line-clamp-3">“{giftCard.message}”</p>}
          </div>
          <div className="shrink-0 text-sm tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</div>
        </div>
      </div>
    </section>
  )
}

function GiftInfoCard({ giftCard }: { giftCard: any }) {
  const recipient = recipientOf(giftCard)
  return (
    <section>
      <h2 className={eyebrow}>Картын мэдээлэл</h2>
      <dl className="mt-2 divide-y divide-border border-y border-border">
        <InfoRow label="Хүлээн авагч">{recipient || '-'}</InfoRow>
        {giftCard?.email && <InfoRow label="Хүлээн авагчийн имэйл">{giftCard.email}</InfoRow>}
        {giftCard?.senderEmail && <InfoRow label="Илгээгчийн имэйл">{giftCard.senderEmail}</InfoRow>}
        <InfoRow label="Зурвас">{giftCard?.message || '-'}</InfoRow>
        <InfoRow label="Илгээх огноо"><span className="tabular-nums">{formatDateTimeLong(giftCard?.deliveryDate)}</span></InfoRow>
        <InfoRow label="Хуулбар авах">{giftCard?.sendCopy ? 'Тийм' : 'Үгүй'}</InfoRow>
        <InfoRow label="Үүсгэсэн"><span className="tabular-nums">{formatDateTimeLong(giftCard?.createdAt)}</span></InfoRow>
        {giftCard?.paidAt && <InfoRow label="Төлсөн"><span className="tabular-nums">{formatDateTimeLong(giftCard.paidAt)}</span></InfoRow>}
        {giftCard?.sentAt && <InfoRow label="Илгээгдсэн"><span className="tabular-nums">{formatDateTimeLong(giftCard.sentAt)}</span></InfoRow>}
        {giftCard?.usedAt && <InfoRow label="Ашиглагдсан"><span className="tabular-nums">{formatDateTimeLong(giftCard.usedAt)}</span></InfoRow>}
        {giftCard?.canceledAt && <InfoRow label="Цуцлагдсан"><span className="tabular-nums">{formatDateTimeLong(giftCard.canceledAt)}</span></InfoRow>}
      </dl>
    </section>
  )
}

function PriceBreakdown({ giftCard }: { giftCard: any }) {
  return (
    <section>
      <h2 className={eyebrow}>Төлбөрийн задаргаа</h2>
      <dl className="mt-2 pt-2 border-t border-border text-[13px]">
        <div className="flex justify-between gap-4 py-1.5">
          <dt className="text-muted-foreground">Картын дүн</dt>
          <dd className="tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</dd>
        </div>
      </dl>
      <div className="mt-2 pt-4 border-t border-border flex items-baseline justify-between gap-4">
        <span className="text-sm text-foreground">Нийт төлөх</span>
        <span className="text-2xl font-light tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</span>
      </div>
    </section>
  )
}

function FooterActions({ pathname }: { pathname: string }) {
  return (
    <div className="pt-6 border-t border-border flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] print:hidden">
      <button type="button" onClick={() => window.print()} className={sweepLink}>Хэвлэх</button>
      <a href="https://help.shop.mn" target="_blank" rel="noopener noreferrer" className={sweepLink}>Тусламж</a>
      <a href={navigateUrl('/gift', pathname)} className={`sm:ml-auto inline-flex items-center gap-1.5 ${sweepLink}`}>
        Шинэ карт авах<ArrowRight className="size-3.5" strokeWidth={1.5} />
      </a>
    </div>
  )
}

function PrintableGiftReceipt({ giftCard, vendor }: { giftCard: any, vendor: any }) {
  const mounted = useSyncExternalStore(subscribeNoop, () => true, () => false)
  if (!mounted) return null
  const recipient = recipientOf(giftCard)
  const label = STATUS_LABELS[giftCard?.status] || '—'
  return createPortal(
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{'@page { size: A4; margin: 16mm; }'}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Бэлгийн картын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-bold">Карт #{giftCard?.customId}</div>
          {giftCard?.createdAt && <div className="text-[11px]">{formatDateTimeLong(giftCard.createdAt)}</div>}
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-bold">{label}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Хүлээн авагч</div>
          <div>{recipient || '—'}</div>
          {giftCard?.email && <div>{giftCard.email}</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Илгээгч</div>
          <div>{giftCard?.senderEmail || '—'}</div>
          {giftCard?.deliveryDate && <div>Илгээх: {formatDateTimeLong(giftCard.deliveryDate)}</div>}
        </div>
      </div>
      {giftCard?.message && (
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold mb-1">Зурвас</div>
          <div className="italic">&ldquo;{giftCard.message}&rdquo;</div>
        </div>
      )}
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-bold">Зүйл</th>
            <th className="text-right py-1 font-bold w-32">Дүн</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-zinc-300 align-top">
            <td className="py-1 pr-2">Бэлгийн карт{giftCard?.template?.name ? ` · ${giftCard.template.name}` : ''}</td>
            <td className="py-1 text-right tabular-nums">{formatPrice(giftCard?.amount)}</td>
          </tr>
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-bold text-[13px]">
          <span>Нийт</span>
          <span className="tabular-nums">{formatPrice(giftCard?.amount)}</span>
        </div>
      </div>
      {giftCard?.code && (
        <div className="mt-4 border border-black p-3">
          <div className="text-[10px] uppercase font-bold mb-1">Картын код</div>
          <div className="text-[16px] font-mono tracking-wider">{giftCard.code}</div>
        </div>
      )}
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>,
    document.body,
  )
}

export default function GiftDetail({ giftCard, vendor }: { giftCard: any, vendor?: any }) {
  const router = useRouter()
  const pathname = usePathname()
  const status = giftCard?.status

  const showPending = status === 'pending'
  const showPaidBanner = ['paid', 'sent', 'used'].includes(status)
  const showCanceled = status === 'canceled'

  return (
    <>
      <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-16 print:hidden">
        <GiftTitle giftCard={giftCard} status={status} />

        {(showPending || showPaidBanner || showCanceled) && (
          <div className="mt-10 lg:mt-12 space-y-8">
            {showPending && <PendingPayment giftCard={giftCard} vendor={vendor} onSuccess={() => router.refresh()} />}
            {showPaidBanner && <PaidBanner giftCard={giftCard} status={status} />}
            {showCanceled && <CanceledBanner giftCard={giftCard} />}
          </div>
        )}

        <div className="mt-12 lg:mt-16 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)] lg:gap-16">
          <div className="min-w-0">
            <GiftItem giftCard={giftCard} />
          </div>
          <aside className="mt-12 lg:mt-0 space-y-10 lg:sticky lg:top-24 lg:self-start">
            <PriceBreakdown giftCard={giftCard} />
            <GiftInfoCard giftCard={giftCard} />
          </aside>
        </div>

        <div className="mt-16"><FooterActions pathname={pathname} /></div>
      </div>
      <PrintableGiftReceipt giftCard={giftCard} vendor={vendor} />
    </>
  )
}
